import { useEffect, useRef } from 'react';
import COS from 'cos-js-sdk-v5';
import useCOSConfig from './useCOSConfig';
import { shallowEqual, useSelector } from 'umi';

const COS_ERROR_CODE = 'COS_ERROR';

const createCOSError = (msg: string, error?: any): any => ({
  ...error,
  code: COS_ERROR_CODE,
  message: msg,
});

export default function useCos({ bucket = '', region = '' } = {}) {
  const cosInstance = useRef<any>(null);
  const baseParams = useRef<{ bucket: string; region: string; }>({
    bucket,
    region,
  });
  const userInfo = useSelector(
    (state: any) => state?.home?.userInfo,
    shallowEqual,
  );

  // 使用全局 COS 配置管理
  const { 
    cachedConfig, 
    getOrRefreshConfig, 
    clearConfig,
    isConfigExpired
  } = useCOSConfig();

  const cachedConfigRef = useRef(cachedConfig);

  useEffect(() => {
    cachedConfigRef.current = cachedConfig;
  }, [cachedConfig]);
  
  const isConfigExpiredVal = isConfigExpired() || !cachedConfig;
  const putObject = async (params: any): Promise<any> => {
    if (!cosInstance.current) {
      return Promise.reject(createCOSError('cos插件未初始化'));
    }
    const currentConfig = cachedConfigRef.current;
    return new Promise((resolve, reject) => {
      const cos = cosInstance.current;
      const { bucket, region } = baseParams.current || {};
      const { path, file, onProgress } = params || {};
      cos.putObject(
        {
          Bucket: currentConfig?.bucket || bucket,
          Region: currentConfig?.region || region,
          Key: path,
          StorageClass: 'STANDARD',
          Body: file,
          onProgress(progressData: any) {
            onProgress instanceof Function && onProgress(progressData);
          },
        },
        (err: any, data: any) => {
          if (err) {
            // 如果是签名过期错误，尝试清除缓存并重试
            if (err.statusCode === 403 || err.code === 'SignatureDoesNotMatch') {
              clearConfig();
            }
            reject(createCOSError('COS putObject方法异常', err));
          } else {
            resolve(data);
          }
        },
      );
    });
  };

  const putObjects = async (params: any[]): Promise<any[]> => {
    if (!Array.isArray(params)) {
      return Promise.reject(createCOSError('批量上传参数异常'));
    }
    
    try {
      const promises = params?.map((item) => putObject(item));
      const results: any[] = await Promise.allSettled(promises);
      
      if (results?.length && results.every((item) => item.status === 'rejected')) {
        throw new Error(results[0].reason);
      }
      
      return results;
    } catch (e) {
      throw e;
    }
  };

  useEffect(() => {
    if(!userInfo) return;
    if (isConfigExpiredVal) {
      if (userInfo) {
        getOrRefreshConfig().catch((error) => {
          console.warn('预加载 COS 配置失败:', error);
        });
      }
    }
  }, [isConfigExpiredVal, userInfo]);

  useEffect(() => {
    if(!userInfo) return;
    // 创建 COS 实例
    cosInstance.current = new COS({
      getAuthorization(options: any, callback: Function) {
        // 使用 async/await 处理异步操作
        (async () => {
          try {
            // 获取最新配置（会自动检查过期并刷新）
            const config = await getOrRefreshConfig();
            
            // 使用缓存的签名时间，确保一致性
            callback({
              TmpSecretId: config.tmp_secret_id || '',
              TmpSecretKey: config.tmp_secret_key || '',
              SecurityToken: config.session_token || '',
              // 使用缓存的签名时间，而不是重新计算
              StartTime: config.signatureStartTime,
              ExpiredTime: config.signatureExpiryTime,
            });
            
          } catch (error) {
            console.error('获取 COS 授权失败:', error);
            // 降级处理：返回一个短期有效的签名
            const now = Math.floor(Date.now() / 1000);
            callback({
              TmpSecretId: '',
              TmpSecretKey: '',
              SecurityToken: '',
              StartTime: now,
              ExpiredTime: now + 60, // 1分钟临时过期
            });
          }
        })();
      },
    });

    // 添加isConfigExpiredVal依赖，isConfigExpiredVal变化时，重新创建COS实例，确保缓存有效，防止重复请求auth
  }, [isConfigExpiredVal,userInfo]);

  return {
    cosInstance,
    putObject,
    putObjects,
    getOrRefreshConfig, // 暴露手动刷新配置的方法
    clearConfig, // 暴露清除缓存的方法
  };
}
